import React, { useEffect, useState } from "react";
import { X, Download, Printer, Mail } from "lucide-react";

/**
 * Iter 64 (#8, #9) — Reusable PDF popout viewer modal.
 * Iter v34{ab} — Added optional `reminderAction` to render a Mail button
 * to the LEFT of Download (used for overdue invoice reminder).
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   pdfUrl: string                  // backend endpoint (with `/api/...`)
 *   fileName?: string               // suggested filename for download
 *   title?: string
 *   showPrint?: boolean             // default true
 *   showDownload?: boolean          // default true
 *   reminderAction?: {              // optional reminder button (Iter v34{ab})
 *     label: string,                // tooltip + visible label
 *     onClick: () => Promise<void>, // handler
 *     enabled: boolean,             // whether button is clickable
 *     testid?: string,              // data-testid attribute
 *   }
 */
export default function PdfViewerModal({
  open, onClose, pdfUrl, fileName = "document.pdf",
  title = "Lihat Dokumen", showPrint = true, showDownload = true,
  reminderAction = null,
}) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !pdfUrl) return;
    let cancelled = false;
    let createdUrl = "";
    setLoading(true); setError(""); setBlobUrl("");
    (async () => {
      try {
        const res = await fetch(pdfUrl, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || "Gagal memuat PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, pdfUrl]);

  if (!open) return null;

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    // Open the PDF in a new tab and trigger print dialog from there.
    const w = window.open(blobUrl, "_blank");
    if (!w) return;
    w.addEventListener("load", () => {
      try { w.print(); } catch (_) { /* ignore */ }
    }, { once: true });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="pdf-viewer-modal"
    >
      <div className="bg-white w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl rounded-sm overflow-hidden">
        <header className="flex items-center justify-between border-b border-stone-200 px-4 py-3 bg-stone-50">
          <h3 className="text-sm font-semibold text-stone-800 truncate" data-testid="pdf-viewer-title">{title}</h3>
          <div className="flex items-center gap-2">
            {reminderAction && (
              <button
                onClick={reminderAction.onClick}
                disabled={!reminderAction.enabled}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                data-testid={reminderAction.testid || "pdf-viewer-reminder-btn"}
                title={reminderAction.label}
              >
                <Mail className="h-3.5 w-3.5" /> {reminderAction.label}
              </button>
            )}
            {showDownload && (
              <button
                onClick={handleDownload}
                disabled={!blobUrl}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                data-testid="pdf-viewer-download-btn"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            )}
            {showPrint && (
              <button
                onClick={handlePrint}
                disabled={!blobUrl}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-stone-300 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                data-testid="pdf-viewer-print-btn"
              >
                <Printer className="h-3.5 w-3.5" /> Print
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-stone-200 rounded-sm"
              aria-label="Tutup"
              data-testid="pdf-viewer-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 bg-stone-100">
          {loading && (
            <div className="h-full flex items-center justify-center text-stone-500 text-sm">
              Memuat PDF...
            </div>
          )}
          {error && (
            <div className="h-full flex items-center justify-center text-red-600 text-sm" data-testid="pdf-viewer-error">
              Gagal memuat PDF: {error}
            </div>
          )}
          {blobUrl && !loading && !error && (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-full border-0"
              data-testid="pdf-viewer-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}
